import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Lock, PenLine, Edit2, Save, Loader2, AlertCircle, CheckCircle2, PlusCircle, History, FileText, Upload, Eye, Trash2, Download } from "lucide-react";

const ADJUSTMENT_CHANNELS = [
  { v: "efectivo", l: "Efectivo (depósito caja fuerte)" },
  { v: "tarjeta", l: "Tarjeta (neto)" },
  { v: "rappi", l: "Rappi" },
  { v: "uber_eats", l: "Uber Eats" },
  { v: "didi", l: "DiDi" },
  { v: "oxxo_pay", l: "OXXO Pay" },
  { v: "venta_total", l: "Venta total" },
];

// Fix WH83 (2026-10-21): documentos del corte (pedido de Gabriela). La ficha de
// valores (WH62) sigue con sus endpoints /fichas-valores; el corte Z y las
// facturas del día usan /documentos/{tipo}. Los data-testid de la ficha no cambian.
const DOCS_CORTE = [
  {
    tipo: "ficha", campo: "fichas_valores", url: "fichas-valores", titulo: "Ficha de valores",
    ayuda: "La que se manda con el efectivo.", boton: "Subir ficha de valores",
    vacio: "Todavía no se subió la ficha de valores de este corte.", accept: "image/*,.pdf", multiple: false,
    subido: "Ficha de valores subida", quitado: "Ficha quitada",
    errSubir: "No se pudo subir la ficha", errVer: "No se pudo abrir la ficha", errQuitar: "No se pudo quitar la ficha",
    tid: { btn: "subir-ficha-btn", input: "subir-ficha-input", vacio: "fichas-vacio", lista: "fichas-list", item: "ficha", ver: "ver-ficha", quitar: "quitar-ficha" },
  },
  {
    tipo: "corte_z", campo: "cortes_z", url: "documentos/corte_z", titulo: "Corte Z",
    ayuda: "El reporte Z del POS. Los de Wansoft se traen solos cada mañana.", boton: "Subir corte Z",
    vacio: "Todavía no se subió el corte Z.", accept: "image/*,.pdf", multiple: false,
    subido: "Corte Z subido", quitado: "Archivo quitado",
    errSubir: "No se pudo subir el archivo", errVer: "No se pudo abrir el archivo", errQuitar: "No se pudo quitar el archivo",
    tid: { btn: "subir-corte-z-btn", input: "subir-corte-z-input", vacio: "cortes-z-vacio", lista: "cortes-z-list", item: "corte-z", ver: "ver-corte-z", quitar: "quitar-corte-z" },
  },
  {
    tipo: "facturas_dia", campo: "facturas_dia", url: "documentos/facturas_dia", titulo: "Facturas del día",
    ayuda: "Foto, PDF o XML. Puedes elegir varios archivos a la vez.", boton: "Subir facturas del día",
    vacio: "Todavía no se subieron las facturas del día.", accept: "image/*,.pdf,.xml,text/xml,application/xml", multiple: true,
    subido: "Facturas del día subidas", quitado: "Archivo quitado",
    errSubir: "No se pudo subir el archivo", errVer: "No se pudo abrir el archivo", errQuitar: "No se pudo quitar el archivo",
    tid: { btn: "subir-facturas-dia-btn", input: "subir-facturas-dia-input", vacio: "facturas-dia-vacio", lista: "facturas-dia-list", item: "factura-dia", ver: "ver-factura-dia", quitar: "quitar-factura-dia" },
  },
];

function DetailRow({ label, value, bold = false }) {
  return (
    <div className="flex justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`text-sm ${bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}

export default function OpsCorteDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [corte, setCorte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [showAdjForm, setShowAdjForm] = useState(false);
  const [adjForm, setAdjForm] = useState({ channel: "efectivo", original_value: "", adjusted_value: "", reason: "", notes: "" });
  const [savingAdj, setSavingAdj] = useState(false);
  // Fix WH62 (2026-09-28): subida de la ficha de valores.
  // Fix WH83 (2026-10-21): guarda el tipo que se está subiendo (ficha, corte_z o facturas_dia).
  const [subiendoDoc, setSubiendoDoc] = useState(null);
  // Fix WH85 (2026-10-22): traer el corte Z de Wansoft a mano (el sync lo hace solo cada mañana).
  const [importandoZ, setImportandoZ] = useState(false);
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const canSeeInvoices = ["corporate_admin", "treasury"].includes(user?.role);
  const canImportarZ = ["corporate_admin", "treasury"].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/ops/cortes/${id}`);
      setCorte(res.data);
      setEditForm({
        observaciones: res.data.observaciones || "",
        total_ordenes: res.data.total_ordenes || 0,
      });
      if (res.data.immutable) {
        const adjRes = await api.get(`/ops/cortes/${id}/adjustments`);
        setAdjustments(adjRes.data || []);
      }
      if (["corporate_admin", "treasury"].includes(user?.role)) {
        try {
          const invRes = await api.get(`/ops/fiscal/invoices?corte_id=${id}`);
          setLinkedInvoices(invRes.data.invoices || []);
        } catch {
          setLinkedInvoices([]);
        }
      }
    } catch {
      toast.error("Corte no encontrado");
      navigate("/ops/cortes");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, user?.role]);

  useEffect(() => { load(); }, [load]);

  const handleSign = async () => {
    const confirmed = window.confirm("Una vez firmado, el corte no se puede editar. ¿Confirmar firma?");
    if (!confirmed) return;
    setSigning(true);
    try {
      await api.post(`/ops/cortes/${id}/sign`);
      toast.success("Corte firmado correctamente");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al firmar");
    } finally {
      setSigning(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      // Fix WH14 (2026-08-30): en un corte generado desde el POS, el gerente
      // sólo puede guardar observaciones (el backend rechaza el resto).
      const soloNotas = corte?.source === "wansoft" && user?.role !== "corporate_admin";
      await api.put(`/ops/cortes/${id}`, soloNotas ? {
        observaciones: editForm.observaciones || null,
      } : {
        observaciones: editForm.observaciones || null,
        total_ordenes: parseInt(editForm.total_ordenes) || 0,
        total_personas: parseInt(editForm.total_ordenes) || 0,
      });
      toast.success("Corte actualizado");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const fmtMXN = v => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
  const canSign = ["corporate_admin", "branch_manager"].includes(user?.role);
  const canEdit = canSign && !corte?.immutable;
  const canAdjust = user?.role === "corporate_admin";

  // Fix WH62 (2026-09-28): ficha de valores que se manda con el efectivo.
  // Se usa corte.id (no el id de la URL): un aviso viejo puede traer el id de un
  // corte repetido que el WH60 archivó, y el backend abre el que quedó.
  // Fix WH83 (2026-10-21): los mismos pasos para los tres documentos (DOCS_CORTE).
  // Facturas del día permite elegir varios archivos: se suben uno por uno.
  const handleSubirDoc = async (cfg, e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const grande = files.find((f) => f.size > 10 * 1024 * 1024);
    if (grande) {
      toast.error(files.length > 1 ? `"${grande.name}" supera 10 MB` : "El archivo no puede superar 10 MB");
      return;
    }
    setSubiendoDoc(cfg.tipo);
    let ok = 0;
    try {
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/ops/cortes/${corte.id}/${cfg.url}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          ok += 1;
        } catch (err) {
          const motivo = err.response?.data?.detail || cfg.errSubir;
          toast.error(files.length > 1 ? `${file.name}: ${motivo}` : motivo);
        }
      }
      if (ok) {
        toast.success(files.length > 1 ? `${ok} de ${files.length} archivos subidos` : cfg.subido);
        load();
      }
    } finally {
      setSubiendoDoc(null);
    }
  };

  // La pestaña se abre en el clic (si se abre después del await, el navegador
  // la bloquea como popup). Si igual la bloquea, se descarga el archivo.
  const handleVerDoc = async (cfg, f) => {
    const win = window.open("", "_blank");
    try {
      const res = await api.get(`/ops/cortes/${corte.id}/${cfg.url}/${f.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (win) win.location.href = url;
      else downloadBlob(res.data, f.nombre || "archivo");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (win) win.close();
      toast.error(cfg.errVer);
    }
  };

  // Fix WH85 (2026-10-22): trae de Wansoft el corte Z de este día. Solo Corporativo y Finanzas.
  const handleImportarZ = async () => {
    setImportandoZ(true);
    try {
      const { data } = await api.post(`/ops/cortes/${corte.id}/importar-corte-z`);
      if (data.importados > 0) {
        toast.success(data.importados > 1 ? `${data.importados} cortes Z traídos de Wansoft` : "Corte Z traído de Wansoft");
        load();
      } else if (data.actualizados > 0) {
        // Fix WH87 (2026-10-23): un Z ya importado se rehízo con formas de pago y retiros.
        toast.success(data.actualizados > 1 ? `${data.actualizados} cortes Z actualizados con formas de pago, retiros y arqueo` : "Corte Z actualizado con formas de pago, retiros y arqueo");
        load();
      } else if (data.sin_z) {
        toast.info("Wansoft no tiene un corte Z con ventas para este día");
      } else {
        toast.info("Los cortes Z de Wansoft ya estaban en este corte");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo traer el corte Z de Wansoft");
    } finally {
      setImportandoZ(false);
    }
  };

  const handleQuitarDoc = async (cfg, f) => {
    if (!window.confirm(`¿Quitar "${f.nombre}" de este corte?`)) return;
    try {
      await api.delete(`/ops/cortes/${corte.id}/${cfg.url}/${f.id}`);
      toast.success(cfg.quitado);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || cfg.errQuitar);
    }
  };

  const handleSaveAdjustment = async () => {
    if ((adjForm.reason || "").trim().length < 5) { toast.error("La justificación debe tener al menos 5 caracteres"); return; }
    if (adjForm.original_value === "" || adjForm.adjusted_value === "") { toast.error("Completa el valor original y el ajustado"); return; }
    setSavingAdj(true);
    try {
      await api.post(`/ops/cortes/${id}/adjustments`, {
        channel: adjForm.channel,
        original_value: parseFloat(adjForm.original_value) || 0,
        adjusted_value: parseFloat(adjForm.adjusted_value) || 0,
        reason: adjForm.reason.trim(),
        notes: adjForm.notes || null,
      });
      toast.success("Nota de ajuste registrada");
      setShowAdjForm(false);
      setAdjForm({ channel: "efectivo", original_value: "", adjusted_value: "", reason: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar el ajuste");
    } finally {
      setSavingAdj(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#FFDF00]" /></div>;
  if (!corte) return null;

  const ch = corte.channels || {};
  const ef = ch.efectivo || {};
  const tar = ch.tarjeta || {};
  const del = ch.delivery || {};

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/ops/cortes")} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-slate-500" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Corte de Caja</h1>
          <p className="text-sm text-slate-500">{corte.branch_name} · {corte.date}</p>
        </div>
        {corte.source === "wansoft" && (
          <span className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold" data-testid="corte-pos-badge">
            POS Wansoft
          </span>
        )}
        {corte.immutable ? (
          <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            <Lock className="w-3.5 h-3.5" /> Firmado
          </span>
        ) : (
          <span className="flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
            Borrador
          </span>
        )}
      </div>

      {corte.source === "wansoft" && corte.fuente_nota && (
        <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-700" data-testid="corte-fuente-nota">
          {corte.fuente_nota}
        </div>
      )}

      {/* Summary */}
      <div className="bg-slate-900 text-white rounded-xl p-5">
        <div className="text-sm text-slate-400 mb-1">Venta total del dia</div>
        <div className="text-4xl font-bold mb-3">{fmtMXN(corte.venta_total)}</div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div><div className="text-xs text-slate-400">Ordenes</div><div className="text-lg font-semibold">{corte.total_ordenes || "—"}</div></div>
          <div><div className="text-xs text-slate-400">Ticket prom.</div><div className="text-lg font-semibold">{corte.ticket_promedio ? fmtMXN(corte.ticket_promedio) : "—"}</div></div>
          <div><div className="text-xs text-slate-400">Personas</div><div className="text-lg font-semibold">{corte.total_personas || "—"}</div></div>
        </div>
      </div>

      {/* Channels */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-1">
        <h2 className="font-semibold text-slate-900 mb-3">Detalle por canal</h2>
        {ef.ventas_brutas != null && (
          <>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wide pt-2">Efectivo</div>
            <DetailRow label="Ventas brutas" value={fmtMXN(ef.ventas_brutas)} />
            <DetailRow label="Retiros" value={fmtMXN(ef.retiros)} />
            <DetailRow label="Deposito caja fuerte" value={fmtMXN(ef.deposito_caja_fuerte)} bold />
          </>
        )}
        {tar.ventas_brutas != null && (
          <>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wide pt-4">Tarjeta ({tar.procesador})</div>
            <DetailRow label="Ventas brutas" value={fmtMXN(tar.ventas_brutas)} />
            <DetailRow label="Comision estimada" value={fmtMXN(tar.comision_estimada)} />
          </>
        )}
        {(del.rappi != null || del.uber_eats != null) && (
          <>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wide pt-4">Delivery</div>
            {del.rappi > 0 && <DetailRow label="Rappi" value={fmtMXN(del.rappi)} />}
            {del.uber_eats > 0 && <DetailRow label="Uber Eats" value={fmtMXN(del.uber_eats)} />}
            {del.didi > 0 && <DetailRow label="DiDi" value={fmtMXN(del.didi)} />}
            {del.oxxo_pay > 0 && <DetailRow label="OXXO Pay" value={fmtMXN(del.oxxo_pay)} />}
          </>
        )}
      </div>

      {/* Fix WH62 (2026-09-28) + WH83 (2026-10-21): documentos del corte. La
          ficha de valores, el corte Z y las facturas del día, cada uno con su
          botón. Se pueden subir con el corte en borrador o firmado: no cambian montos. */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="corte-fichas-valores">
        <div>
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4" /> Documentos del corte</h2>
          <p className="text-xs text-slate-500 mt-0.5">Foto o PDF, hasta 10 MB cada archivo. Se pueden subir con el corte en borrador o ya firmado.</p>
        </div>
        {DOCS_CORTE.map((cfg) => {
          const docs = corte[cfg.campo] || [];
          return (
            <div key={cfg.tipo} className="pt-4 border-t border-slate-100 space-y-2" data-testid={`docs-${cfg.tipo}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">{cfg.titulo}</h3>
                  <p className="text-xs text-slate-500">{cfg.ayuda}</p>
                  {cfg.tipo === "corte_z" && canImportarZ && (
                    <button type="button" onClick={handleImportarZ} disabled={importandoZ || !!subiendoDoc} data-testid="importar-corte-z-btn"
                      className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 hover:bg-slate-50 transition-colors disabled:opacity-50">
                      {importandoZ ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      {importandoZ ? "Buscando en Wansoft..." : "Traer de Wansoft"}
                    </button>
                  )}
                </div>
                <label
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-[#FFDF00] hover:bg-yellow-300 transition-colors cursor-pointer ${subiendoDoc ? "opacity-50 pointer-events-none" : ""}`}
                  data-testid={cfg.tid.btn}
                >
                  {subiendoDoc === cfg.tipo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {subiendoDoc === cfg.tipo ? "Subiendo..." : cfg.boton}
                  <input type="file" accept={cfg.accept} multiple={cfg.multiple} className="hidden" onChange={(e) => handleSubirDoc(cfg, e)} disabled={!!subiendoDoc} data-testid={cfg.tid.input} />
                </label>
              </div>
              {docs.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid={cfg.tid.vacio}>{cfg.vacio}</p>
              ) : (
                <div className="divide-y divide-slate-100" data-testid={cfg.tid.lista}>
                  {docs.map(f => (
                    <div key={f.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`${cfg.tid.item}-${f.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">
                          {f.nombre}
                          {f.origen === "wansoft" && (
                            <span className="ml-2 align-middle px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-wide" data-testid={`wansoft-${f.id}`}>Wansoft</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{f.subida_por_nombre} · {new Date(f.subida_at).toLocaleString("es-MX")}</div>
                        {/* Fix WH88 (2026-10-23): cajero y diferencia de arqueo del Z de Wansoft, sin abrir el PDF */}
                        {f.origen === "wansoft" && (f.wansoft_cajero || f.wansoft_arqueo_diferencia != null) && (
                          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5" data-testid={`wansoft-arqueo-${f.id}`}>
                            {f.wansoft_cajero && <span>Cajero: {f.wansoft_cajero}</span>}
                            {f.wansoft_arqueo_diferencia != null && (Math.abs(f.wansoft_arqueo_diferencia) >= 0.01 ? (
                              <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 font-semibold">Diferencia de arqueo {fmtMXN(f.wansoft_arqueo_diferencia)}</span>
                            ) : (
                              <span className="text-emerald-700">Arqueo sin diferencia</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => handleVerDoc(cfg, f)} data-testid={`${cfg.tid.ver}-${f.id}`}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 hover:bg-slate-50">
                          <Eye className="w-3.5 h-3.5" /> Ver
                        </button>
                        {(user?.role === "corporate_admin" || f.subida_por === user?.id) && (
                          <button onClick={() => handleQuitarDoc(cfg, f)} data-testid={`${cfg.tid.quitar}-${f.id}`}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" /> Quitar
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Factura global emitida a partir de este corte */}
      {canSeeInvoices && (
        <div data-testid="corte-facturas-panel" className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Facturación</div>
          {linkedInvoices.length === 0 ? (
            <p className="text-sm text-slate-500">Este corte todavía no tiene una factura global emitida.</p>
          ) : (
            <div className="space-y-2">
              {linkedInvoices.map(inv => (
                <button
                  key={inv.id}
                  data-testid={`corte-factura-${inv.id}`}
                  onClick={() => navigate("/ops/fiscal/facturacion")}
                  className="w-full text-left flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                >
                  <div>
                    <div className="text-sm font-semibold text-slate-800">
                      {inv.internal_serie}{inv.internal_folio}
                      {inv.is_demo && (
                        <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1">DEMO</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">{inv.uuid_fiscal || "sin UUID"}</div>
                  </div>
                  <div className="text-sm font-bold text-slate-900">{fmtMXN(inv.total)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      {!editing && corte.observaciones && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Observaciones</div>
          <p className="text-sm text-slate-700 italic">"{corte.observaciones}"</p>
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <div className="bg-white rounded-xl border border-yellow-300 p-5 space-y-3">
          <h2 className="font-semibold text-slate-900">{corte.source === "wansoft" && user?.role !== "corporate_admin" ? "Agregar observaciones" : "Editar"}</h2>
          {/* Fix WH14 (2026-08-30): en cortes del POS el gerente no edita conteos */}
          {!(corte.source === "wansoft" && user?.role !== "corporate_admin") && (
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Total ordenes</label>
            <input
              type="number"
              value={editForm.total_ordenes}
              onChange={e => setEditForm(p => ({ ...p, total_ordenes: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              data-testid="edit-total-ordenes"
            />
          </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Observaciones</label>
            <textarea
              value={editForm.observaciones}
              onChange={e => setEditForm(p => ({ ...p, observaciones: e.target.value }))}
              rows={2}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] resize-none"
              data-testid="edit-observaciones"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} className="flex-1 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50 transition-colors">
              Cancelar
            </button>
            <button onClick={handleSaveEdit} disabled={saving} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-[#FFDF00] text-sm font-semibold hover:bg-yellow-300 transition-colors disabled:opacity-50" data-testid="confirm-edit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
          </div>
        </div>
      )}

      {corte.immutable && (
        <div className="flex items-center gap-2 p-4 bg-green-50 rounded-xl border border-green-200 text-sm text-green-700">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          Corte firmado por {corte.signed_by_name || "un usuario"}. Para corregir, agrega una nota de ajuste (no rompe la inmutabilidad).
        </div>
      )}

      {/* Notas de ajuste */}
      {corte.immutable && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="corte-adjustments-section">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2"><History className="w-4 h-4" /> Notas de ajuste</h2>
            {canAdjust && !showAdjForm && (
              <button onClick={() => setShowAdjForm(true)} data-testid="add-adjustment-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 hover:bg-slate-50">
                <PlusCircle className="w-3.5 h-3.5" /> Nueva nota de ajuste
              </button>
            )}
          </div>

          {showAdjForm && (
            <div className="rounded-xl border border-yellow-300 bg-yellow-50/50 p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Canal</label>
                  <select value={adjForm.channel} onChange={e => setAdjForm(p => ({ ...p, channel: e.target.value }))}
                    data-testid="adjustment-channel-select"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                    {ADJUSTMENT_CHANNELS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Valor original</label>
                  <input type="number" step="0.01" value={adjForm.original_value}
                    onChange={e => setAdjForm(p => ({ ...p, original_value: e.target.value }))}
                    data-testid="adjustment-original-input"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Valor corregido</label>
                  <input type="number" step="0.01" value={adjForm.adjusted_value}
                    onChange={e => setAdjForm(p => ({ ...p, adjusted_value: e.target.value }))}
                    data-testid="adjustment-adjusted-input"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Justificación (obligatoria, mín. 5 caracteres)</label>
                <textarea value={adjForm.reason} onChange={e => setAdjForm(p => ({ ...p, reason: e.target.value }))}
                  rows={2} data-testid="adjustment-reason-input"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] resize-none"
                  placeholder="Motivo de la corrección..." />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowAdjForm(false)} className="flex-1 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50 transition-colors">
                  Cancelar
                </button>
                <button onClick={handleSaveAdjustment} disabled={savingAdj} data-testid="confirm-adjustment-btn"
                  className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-[#FFDF00] text-sm font-semibold hover:bg-yellow-300 transition-colors disabled:opacity-50">
                  {savingAdj ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar nota
                </button>
              </div>
            </div>
          )}

          {adjustments.length === 0 ? (
            <p className="text-sm text-slate-400">Sin notas de ajuste registradas. El corte original queda firme.</p>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="adjustments-list">
              {adjustments.map(a => (
                <div key={a.id} className="py-3" data-testid={`adjustment-row-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900">{ADJUSTMENT_CHANNELS.find(c => c.v === a.channel)?.l || a.channel}</span>
                    <span className={`text-sm font-bold ${a.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {a.delta > 0 ? "+" : ""}{fmtMXN(a.delta)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{fmtMXN(a.original_value)} → {fmtMXN(a.adjusted_value)} · {a.created_by_email} · {new Date(a.created_at).toLocaleString("es-MX")}</div>
                  <div className="text-xs text-slate-600 italic mt-1">"{a.reason}"</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {!corte.immutable && canSign && (
        <div className="flex gap-3">
          {canEdit && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-slate-200 font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
              data-testid="edit-corte-btn"
            >
              <Edit2 className="w-4 h-4" /> Editar
            </button>
          )}
          <button
            onClick={handleSign}
            disabled={signing}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
            data-testid="sign-corte-btn"
          >
            {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
            {signing ? "Firmando..." : "Firmar corte"}
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-slate-400 pt-1">
        <AlertCircle className="w-3.5 h-3.5" />
        {corte.immutable
          ? `Firmado por ${corte.signed_by_name || "un usuario"} el ${new Date(corte.signed_at).toLocaleDateString("es-MX")}`
          : "Una vez firmado, no se puede editar"}
      </div>
    </div>
  );
}
